/* eslint-disable @typescript-eslint/no-explicit-any */
import { oc } from 'ts-optchain';

import { core } from '@tock/coretypes';
import { filter, find, includes, map, matches, sortBy } from 'es-toolkit/compat';
import { isEventVariety, isTicketTypeEventVariety } from '@tock/shared/utils/offerings';
import TableTypeConstants from 'constants/TableTypeConstants';
import TicketGroupStateConstants from 'constants/TicketGroupStateConstants';

const ticketGroupHelpers = {
  /**
   * Determines whether a ticketGroup is communal
   *
   * @param {Object} ticketGroup The ticket group to check
   * @returns {bool} Whether the ticket group is for a communal table
   */
  isCommunal(ticketGroup: core.TicketGroup) {
    const COMMUNAL_TABLE_VARIETIES = [TableTypeConstants.COMMUNAL_TABLE];
    return includes(COMMUNAL_TABLE_VARIETIES, ticketGroup.tableVariety);
  },

  /**
   * Determines whether a ticketGroup is non-communal
   *
   * @param {Object} ticketGroup The ticket group to check
   * @returns {bool} Whether the ticket group is for a non-communal table
   */
  isNonCommunal(ticketGroup: core.TicketGroup) {
    const NON_COMMUNAL_TABLE_VARIETIES = [TableTypeConstants.TABLE];
    return includes(NON_COMMUNAL_TABLE_VARIETIES, ticketGroup.tableVariety);
  },

  /**
   * Determines whether a ticketGroup is any type of slot (communal table or standard - not time flexible)
   */
  isSlot(ticketGroup: core.TicketGroup) {
    return !ticketGroup.tableId?.length;
  },

  /**
   * Determines whether a ticketGroup is for an event
   *
   * @param {Object} ticketGroup The ticket group to check
   */
  isEventTicketType(ticketGroup: core.TicketGroup) {
    const variety = ticketGroup.ticketType[0].variety;
    return isEventVariety(variety);
  },

  /**
   * Determines whether a ticketGroup is for an event that uses mobile tickets
   *
   * @param {Object} ticketGroup The ticket group to check
   */
  isMobileTicketEnabledTicketType(ticketGroup: core.TicketGroup) {
    const ticketType = ticketGroup.ticketType[0];
    return !!ticketType.isUsingMobileTicket;
  },

  /**
   * Determines whether a ticketGroup is for a delivery
   *
   * @param {Object} ticketGroup The ticket group to check
   */
  isDeliveryTicketType(ticketGroup: core.TicketGroup) {
    const ticketType = ticketGroup.ticketType[0];
    return ticketType.variety === 'ORDER_DELIVERY';
  },

  /**
   * Calculates the price for a ticket group given similar ticket groups
   * If somehow there's a mismatch between available, held, or sold sold prices, it prefers the available
   * price over the held price, and held over sold.
   * This is there to account for existing bad data (Code was written on 5/30/2015 -> Commit hash 5ec82c2)
   * Returns -1 if there are no similar ticket groups
   * @param {Object} ticketGroup The ticket group for which we should calculate the price
   * @param {Array} providedSimilarTicketGroups An array of similar ticket groups
   * @returns {Number} The price of similar ticket groups, or -1 if there are none
   */
  getPriceGivenSimilarTicketGroups(
    ticketGroup: core.TicketGroup,
    providedSimilarTicketGroups: any
  ) {
    const similarTicketGroups = providedSimilarTicketGroups;
    // Look up similar tickets from the calendar and choose that price.
    let availablePrice: any = null;
    let heldPrice: any = null;
    let price = -1; // To distinguish from when similar tickets are priced at zero
    similarTicketGroups.forEach((tg: any) => {
      if (tg.state === TicketGroupStateConstants.AVAILABLE) {
        availablePrice = tg.type[0].amount;
      }
      if (tg.state === TicketGroupStateConstants.HELD) {
        heldPrice = tg.type[0].amount;
      }
      price = tg.type[0].amount;
    });
    return availablePrice || heldPrice || price;
  },

  /*
   * Calculates the price for a ticket group given similar ticket groups in the calendar
   */
  getPriceGivenCalendar(ticketGroup: core.TicketGroup, calendar: any) {
    return this.getPriceGivenSimilarTicketGroups(
      ticketGroup,
      this.getSimilarTicketGroups(calendar, ticketGroup)
    );
  },

  /**
   * Returns an array of ticket groups with the same ticket type, dateTime, and tableType
   * (meaning maxSize, minPurchaseSize, and maxPurchaseSize).
   * @param {Array} calendar The calendar to find similar ticket groups from.
   * @param {TicketGroup} ticketGroup The ticketGroup to find similar ticket groups for
   * @returns {TicketGroup[]} Array of similar ticket groups, matching the above criteria
   */
  getSimilarTicketGroups(calendar: any, ticketGroup: core.TicketGroup) {
    if (!ticketGroup || !ticketGroup.dateTime) {
      return [];
    }
    return filter(calendar[ticketGroup.dateTime], (tg: core.TicketGroup) => {
      if (tg.id === ticketGroup.id) {
        return true;
      }
      const sameKind =
        tg.tableVariety === TableTypeConstants.COMMUNAL_TABLE ||
        matches({
          dateTime: ticketGroup.dateTime,
          maxSize: ticketGroup.maxSize,
          minPurchaseSize: ticketGroup.minPurchaseSize,
          maxPurchaseSize: ticketGroup.maxPurchaseSize,
        })(tg);

      return sameKind && oc(tg).type[0].id() === oc(ticketGroup).type[0].id();
    });
  },

  /**
   * Given a list of the currently active ticket types, all ticket types, and a
   * ticket group, returns the list of valid ticket types for the ticket group.
   * @param {Object} initialTicketGroup Ticket group in question
   * @param {Array} activeTicketTypes Currently active ticket types
   * @param {Array} allTicketTypes All ticket types, including archived
   * @returns {Array} A list of valid ticket types for the group
   */
  getAllowedTicketTypes(
    initialTicketGroup: core.TicketGroup,
    activeTicketTypes: core.TicketType[],
    allTicketTypes: core.TicketType[],
    isNewTicketGroup: boolean
  ) {
    const allowedTicketTypes = activeTicketTypes;
    const initialTicketTypePrice = initialTicketGroup.type[0];
    let initialTicketTypeIsNotActive = false;
    let initialTicketType: any = null;
    if (initialTicketTypePrice) {
      initialTicketType = find(allowedTicketTypes, {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        id: initialTicketTypePrice.id!,
      });
      initialTicketTypeIsNotActive = !initialTicketType;
    }

    if (initialTicketTypeIsNotActive) {
      initialTicketType = find(allTicketTypes, {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        id: initialTicketTypePrice.id!,
      });
      if (initialTicketType) {
        allowedTicketTypes.push(initialTicketType);
      }
    }

    // If there's an initial ticket type that's an event, don't allow any other ticket type.
    if (initialTicketType && isTicketTypeEventVariety(initialTicketType)) {
      return [initialTicketType];
    }

    // For existing ticket groups (that aren't events), disable changing the ticket group to an event ticket type
    // For new ticket ticket groups, disallow general admission events
    return sortBy(
      filter(allowedTicketTypes, (ticketType) => {
        return !isTicketTypeEventVariety(ticketType) || isNewTicketGroup;
      }),
      'ordering'
    );
  },

  /**
   * Function that returns an updated copy of the provided ticket group
   * with the fees, and types arrays filled in using the information
   * on the provided ticketType
   * @param {Object} ticketGroup The ticket group to be modified
   * @param {Object} ticketType The ticketType from which to pull fee, option, and type information
   * @returns {Object} An updated copy of the ticketGroup
   */
  updateWithTicketType(ticketGroup: core.ITicketGroup, ticketType: core.TicketType) {
    const feesForTicketGroup: core.IFeePrice[] = map(ticketType.fee, (fee: core.ITicketTypeFee) => {
      return {
        isPostSubtotal: false,
        price: {
          id: fee.id,
          name: fee.name,
          amount: fee.priceCents,
        },
      };
    });
    // Return a copy
    return {
      ...ticketGroup,
      type: [{ id: ticketType.id, name: ticketType.name, amount: ticketType.defaultPrice }],
      fee: feesForTicketGroup,
    };
  },
};

export default ticketGroupHelpers;
